// src/components/MessageDetailModal.jsx

import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Button,
  Form,
  Spinner,
  Image,
  InputGroup
} from 'react-bootstrap';
import {
  collection,
  query,
  where,
  onSnapshot,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  arrayUnion,
  Timestamp
} from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { firestore } from '../views/firebaseConfig';
import 'bootstrap/dist/css/bootstrap.min.css';

/**
 * A single message bubble styled like iOS.
 */
function MessageBubble({ msg, currentUserId }) {
  const isCurrent = msg.senderID === currentUserId;
  const darkMode = window.matchMedia?.('(prefers-color-scheme: dark)').matches;

  const bubbleStyle = {
    display: 'inline-block',
    padding: '8px 12px',
    borderRadius: '18px',
    backgroundColor: isCurrent
      ? darkMode
        ? 'rgba(255,255,255,0.15)'
        : '#f0f0f0'
      : darkMode
      ? 'rgba(108,0,108,0.9)'
      : 'rgba(90,0,90,0.9)',
    color: isCurrent ? (darkMode ? '#fff' : '#000') : '#fff',
    maxWidth: '75%',
    marginBottom: '4px'
  };

  const tsDate = msg.timestamp?.toDate
    ? msg.timestamp.toDate()
    : new Date(msg.timestamp);
  const timeFormatted = tsDate.toLocaleString([], {
    hour: 'numeric',
    minute: '2-digit',
    month: 'short',
    day: 'numeric'
  });

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: isCurrent ? 'flex-end' : 'flex-start',
        padding: '4px 0'
      }}
    >
      <div style={bubbleStyle}>
        <div>{msg.text}</div>
        <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '4px' }}>
          {timeFormatted}
        </div>
      </div>
    </div>
  );
}

/**
 * MessageDetailModal props:
 *   - show, onClose
 *   - forsaleId: string
 *   - interestedId: string (current user UID)
 *   - receiverId: string (other party’s UID)
 *   - [optional] conversationId: string (Firestore document ID)
 */
export default function MessageDetailModal({
  show,
  onClose,
  forsaleId,
  interestedId,
  receiverId,
  conversationId: propConversationId // may come from parent
}) {
  const { user, loading: authLoading } = useAuth();
  const currentUserId = user?.uid;

  // Determine “other party”:
  const otherPartyId =
    receiverId === currentUserId ? interestedId : receiverId;

  const [conversationDocId, setConversationDocId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [newMessageText, setNewMessageText] = useState('');
  const [receiverNickname, setReceiverNickname] = useState('Unknown User');
  const [receiverProfileUrl, setReceiverProfileUrl] = useState(null);
  const [forsaleTitle, setForsaleTitle] = useState('');
  const [isCommunicationBlocked, setIsCommunicationBlocked] = useState(false);
  const [showAlertTooLong, setShowAlertTooLong] = useState(false);
  const [showAlertUnavailable, setShowAlertUnavailable] = useState(false);

  const scrollRef = useRef(null);
  const newMessageTextLimit = 160;
  // Allow common punctuation (including ? and !)
  const allowedCharsRegex = /^[a-zA-Z0-9@,\.\-\_'\+\!\?\(\)\s]*$/;

  //
  // 1) If parent passed a conversationId, use it. Otherwise “find or create.”
  //
  useEffect(() => {
    if (
      !show ||
      authLoading ||
      !currentUserId ||
      !otherPartyId ||
      !forsaleId
    ) {
      return;
    }

    // If a propConversationId was provided, skip lookup
    if (propConversationId) {
      setConversationDocId(propConversationId);
      setLoaded(true);
      return;
    }

    // Otherwise, run “find or create” logic
    async function findOrCreateConversation() {
      const inboxColl = collection(firestore, 'globalinbox');
      const q = query(
        inboxColl,
        where('forsaleId', '==', forsaleId),
        where('interestedId', 'in', [currentUserId, otherPartyId]),
        where('receiverId',   'in', [currentUserId, otherPartyId])
      );

      try {
        const snap = await getDocs(q);
        if (!snap.empty) {
          // Reuse existing document ID
          setConversationDocId(snap.docs[0].id);
        } else {
          // Create new conversation doc
          const newConversationId = crypto.randomUUID();
          const newData = {
            conversationId: newConversationId,
            forsaleId,
            receiverId: otherPartyId,
            interestedId: currentUserId,
            conversation: [],
            blockedConversation: false,
            deletedConversation: {},
            lastModified: serverTimestamp()
          };
          const newDocRef = await addDoc(inboxColl, newData);
          setConversationDocId(newDocRef.id);
        }
        setLoaded(true);
      } catch (err) {
        console.error('Error in findOrCreateConversation:', err);
        setShowAlertUnavailable(true);
      }
    }

    findOrCreateConversation();
  }, [
    show,
    authLoading,
    currentUserId,
    otherPartyId,
    forsaleId,
    propConversationId
  ]);

  //
  // 2) Once we have a real document ID, attach onSnapshot listener
  //
  useEffect(() => {
    if (!conversationDocId || !currentUserId) return;
    setLoaded(false);

    const docRef = doc(firestore, 'globalinbox', conversationDocId);
    const unsubscribe = onSnapshot(
      docRef,
      async (snap) => {
        if (!snap.exists()) {
          setShowAlertUnavailable(true);
          return;
        }
        const data = snap.data();
        const convArray = Array.isArray(data.conversation) ? data.conversation : [];
        const updatedConv = [];
        const loadedMsgs = [];

        // Convert incoming “unread” → “read”
        for (let msgData of convArray) {
          let { senderID, text, timestamp, readStatus } = msgData;
          let updatedReadStatus = readStatus;
          if (senderID !== currentUserId && readStatus === 'unread') {
            updatedReadStatus = 'read';
          }
          updatedConv.push({
            senderID,
            text,
            timestamp,
            readStatus: updatedReadStatus
          });
          loadedMsgs.push({
            senderID,
            text,
            timestamp: timestamp?.toDate
              ? timestamp.toDate()
              : new Date(timestamp),
            readStatus: updatedReadStatus
          });
        }

        // Write back if any readStatus changed
        const needsUpdate = convArray.some(
          (m, idx) => m.readStatus !== updatedConv[idx].readStatus
        );
        if (needsUpdate) {
          try {
            await updateDoc(docRef, { conversation: updatedConv });
          } catch (err) {
            console.error('Error updating readStatus:', err);
          }
        }

        // Sort ascending by timestamp
        loadedMsgs.sort((a, b) => a.timestamp - b.timestamp);
        setMessages(loadedMsgs);
        setLoaded(true);

        // Pull block status from document
        if (data.blockComms) {
          setIsCommunicationBlocked(data.blockComms.includes(currentUserId));
        } else {
          setIsCommunicationBlocked(false);
        }
      },
      (err) => {
        console.error('Error listening to conversation:', err);
      }
    );

    return () => {
      unsubscribe();
      setMessages([]);
      setLoaded(true);
    };
  }, [conversationDocId, currentUserId]);

  //
  // 3) Fetch other party’s profile (nickname + avatar)
  //
  async function fetchReceiverProfile() {
    if (!otherPartyId) return;
    try {
      const upSnap = await getDoc(doc(firestore, 'userprofiles', otherPartyId));
      if (upSnap.exists()) {
        const d = upSnap.data();
        if (d.nickname) setReceiverNickname(d.nickname);
        if (d.profileImageUrl) setReceiverProfileUrl(d.profileImageUrl);
      }
    } catch (err) {
      console.error('Error fetching receiver profile:', err);
    }
  }

  //
  // 4) Fetch forsale title when appropriate
  //
  async function fetchForsaleTitle() {
    if (!forsaleId || forsaleId === 'nearbyChat') return;
    try {
      const fsSnap = await getDoc(doc(firestore, 'forsale', forsaleId));
      if (fsSnap.exists()) {
        const d = fsSnap.data();
        if (d.title) setForsaleTitle(d.title);
      }
    } catch (err) {
      console.error('Error fetching forsale title:', err);
    }
  }

  //
  // 5) On “show,” immediately kick off profile + title fetch
  //
  useEffect(() => {
    if (!show || authLoading) return;
    fetchReceiverProfile();
    fetchForsaleTitle();
  }, [show, authLoading, otherPartyId, forsaleId]);

  //
  // 6) Send a new message
  //
  async function sendMessage() {
    const trimmed = newMessageText.trim();
    if (!trimmed) return;
    if (trimmed.length > newMessageTextLimit) {
      setShowAlertTooLong(true);
      return;
    }
    if (isCommunicationBlocked) return;

    if (!conversationDocId) {
      // shouldn’t happen once “loaded” is true
      return;
    }

    const msgObj = {
      senderID: currentUserId,
      receiverID: otherPartyId,
      text: trimmed,
      timestamp: Timestamp.fromDate(new Date()),
      readStatus: 'unread'
    };

    try {
      // Directly update the known doc:
      const docRef = doc(firestore, 'globalinbox', conversationDocId);
      await updateDoc(docRef, {
        conversation: arrayUnion(msgObj),
        lastModified: serverTimestamp(),
        [`deletedConversation.${currentUserId}`]: 'active',
        [`deletedConversation.${otherPartyId}`]: 'active'
      });
      setNewMessageText('');
    } catch (err) {
      console.error('Error sending message:', err);
      setShowAlertUnavailable(true);
    }
  }

  //
  // 7) Auto-scroll whenever messages change
  //
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  if (authLoading) return null;

  return (
    <>
      <Modal
        show={show}
        onHide={onClose}
        centered
        // Use a responsive max-height instead of fixed height
        dialogClassName="message-detail-dialog"
      >
        <Modal.Header
          closeButton={false}
          style={{
            backgroundColor: '#2c2c2e',       // slightly lighter dark gray
            borderBottom: '1px solid #444'
          }}
          className="text-light"
        >
          <div className="d-flex align-items-center w-100">
            {/* ← Back button */}
            <Button
              variant="link"
              className="text-light me-3 p-0"
              style={{ fontSize: '1.5rem' }}
              onClick={onClose}
            >
              ←
            </Button>

            {receiverProfileUrl ? (
              <Image
                src={receiverProfileUrl}
                roundedCircle
                width={45}
                height={45}
                className="me-2"
              />
            ) : (
              <Image
                src="/default-avatar.png"
                roundedCircle
                width={45}
                height={45}
                className="me-2"
              />
            )}

            <div className="me-auto">
              <div style={{ fontWeight: '500', color: '#fff' }}>
                {receiverNickname}
              </div>
              {forsaleTitle && (
                <div style={{ fontSize: '0.9rem', color: '#ccc' }}>
                  {forsaleTitle}
                </div>
              )}
            </div>

            {/* Block/Unblock button */}
            <Button
              variant={
                isCommunicationBlocked ? 'outline-success' : 'outline-danger'
              }
              size="sm"
              onClick={async () => {
                if (!conversationDocId) return;
                const docRef = doc(
                  firestore,
                  'globalinbox',
                  conversationDocId
                );
                if (!isCommunicationBlocked) {
                  // Block
                  await updateDoc(docRef, {
                    blockComms: currentUserId,
                    blockedConversation: true,
                    lastModified: serverTimestamp()
                  });
                  setIsCommunicationBlocked(true);
                  onClose(); // immediately close
                } else {
                  // Unblock
                  await updateDoc(docRef, {
                    blockComms: null,
                    blockedConversation: false,
                    lastModified: serverTimestamp()
                  });
                  setIsCommunicationBlocked(false);
                }
              }}
            >
              {isCommunicationBlocked ? 'Unblock Chat' : 'Block Chat'}
            </Button>
          </div>
        </Modal.Header>

        <Modal.Body
          className="bg-dark text-light p-3"
          style={{
            backgroundColor: '#1c1c1e',
            maxHeight: '40vh',       // responsive max-height
            overflowY: 'auto'
          }}
          ref={scrollRef}
        >
          {!loaded ? (
            <div className="text-center my-3">
              <Spinner animation="border" variant="light" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center text-muted">No messages yet</div>
          ) : (
            messages.map((msg, idx) => (
              <MessageBubble
                key={idx}
                msg={msg}
                currentUserId={currentUserId}
              />
            ))
          )}
        </Modal.Body>

        <Modal.Footer
          className="bg-dark"
          style={{
            backgroundColor: '#1c1c1e',
            borderTop: '1px solid #444'
          }}
        >
          <Form
            className="w-100 d-flex align-items-center"
            onSubmit={(e) => {
              e.preventDefault();
              sendMessage();
            }}
          >
            <InputGroup className="flex-grow-1">
              <Form.Control
                type="text"
                placeholder="Type your message…"
                value={newMessageText}
                onChange={(e) => {
                  const val = e.target.value;
                  if (
                    val.length <= newMessageTextLimit &&
                    allowedCharsRegex.test(val)
                  ) {
                    setNewMessageText(val);
                  }
                }}
                className="bg-secondary text-light border-0"
                style={{ backgroundColor: '#2c2c2e' }}
              />
              <InputGroup.Text className="bg-dark text-light border-0">
                {`${newMessageTextLimit - newMessageText.length}`}
              </InputGroup.Text>
            </InputGroup>

            <Button
              variant="primary"
              type="submit"
              className="ms-2"
              disabled={
                !newMessageText.trim() ||
                isCommunicationBlocked ||
                !loaded
              }
            >
              Send
            </Button>
          </Form>
        </Modal.Footer>
      </Modal>

      {/* ── Alert: Message Too Long ────────────────────────────── */}
      <Modal
        show={showAlertTooLong}
        onHide={() => setShowAlertTooLong(false)}
        centered
      >
        <Modal.Body>
          <strong>Message Too Long</strong>
          <p>
            The message cannot exceed {newMessageTextLimit} characters.
          </p>
          <div className="text-end">
            <Button
              variant="primary"
              onClick={() => setShowAlertTooLong(false)}
            >
              OK
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* ── Alert: Conversation Unavailable ─────────────────────── */}
      <Modal
        show={showAlertUnavailable}
        onHide={() => {
          setShowAlertUnavailable(false);
          onClose();
        }}
        centered
      >
        <Modal.Body>
          <strong>Conversation Unavailable</strong>
          <p>This conversation is no longer available.</p>
          <div className="text-end">
            <Button
              variant="primary"
              onClick={() => {
                setShowAlertUnavailable(false);
                onClose();
              }}
            >
              Close
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
